import { ALL_BRANDS_URL_ID } from "@faire/retailer-shared/categories/urlId";
import { IBreadcrumbOption } from "@faire/retailer-visitor-shared/components/Breadcrumb/index";
import { getAllBrandsLink } from "@faire/retailer-visitor-shared/consts/allBrandsLinks";
import { BRAND_VALUES } from "@faire/retailer-visitor-shared/consts/BRAND_VALUES";
import { Category } from "@faire/retailer-visitor-shared/models/Category/Category";
import { IUrlId } from "@faire/retailer-visitor-shared/models/Category/IUrlId";
import { categoryPath } from "@faire/retailer-visitor-shared/paths/category/categoryPath";
import { suppliersPath } from "@faire/retailer-visitor-shared/paths/suppliers/suppliersPath";
import { useRetailerRoutingContext } from "@faire/retailer-visitor-shared/RetailerRoutingContextProvider";
import { getCategoryDisplayName } from "@faire/retailer-visitor-shared/services/Categories";
import { StrictLocalizeFunction } from "@faire/web--source/common/localization";
import { IRoutingContext } from "@faire/web--source/ui/routing/RoutingContext";
import { match } from "@faire/web-api--source/routes/www/suppliers/c1SuppliersUrlId/brand-value/brandValue/match";
import { useMemo } from "react";
import { useLocation } from "react-router-dom";

export const useGetSuppliersBreadcrumbOptions = (
  category: Category,
  strictLocalize: StrictLocalizeFunction
) => {
  const { pathname } = useLocation();
  const routingContext = useRetailerRoutingContext();
  const brandValue = match(pathname)?.params.brandValue;

  return useMemo(() => {
    const allBrandsBreadcrumb = {
      label: getCategoryDisplayName(ALL_BRANDS_URL_ID, strictLocalize),
      path: getAllBrandsLink(),
    };
    const breadCrumbOptions: IBreadcrumbOption[] = [];

    breadCrumbOptions.push(allBrandsBreadcrumb);
    if (brandValue) {
      const brandValueBreadcrumb = {
        label:
          BRAND_VALUES(strictLocalize)[brandValue?.replace(/-/g, "_")]?.label ??
          brandValue,
      };
      breadCrumbOptions.push(brandValueBreadcrumb);
    } else if (!category.isAllBrands) {
      // avoids duplicate "All Brands" breadcrumb
      breadCrumbOptions.push(
        ...recursivelyGetCategoryBreadcrumbs(
          category,
          strictLocalize,
          routingContext
        ).reverse()
      );
    }
    const lastBreadCrumbOption =
      breadCrumbOptions[breadCrumbOptions.length - 1];
    // last breadcrumbOption should not have path
    if (lastBreadCrumbOption) {
      lastBreadCrumbOption.path = undefined;
    }
    return {
      breadCrumbOptions,
    };
  }, [strictLocalize, brandValue, category, routingContext]);
};

const getBreadCrumbOption = (
  categoryName: string,
  urlIds: [IUrlId, ...IUrlId[]],
  strictLocalize: StrictLocalizeFunction,
  useSuppliersPath: boolean = true,
  routingContext: IRoutingContext
) => {
  return {
    label: getCategoryDisplayName(categoryName, strictLocalize),
    path: useSuppliersPath
      ? suppliersPath({ urlIds, routingContext })
      : categoryPath({ urlIds, routingContext }),
  };
};

const recursivelyGetCategoryBreadcrumbs = (
  category: Category | undefined,
  strictLocalize: StrictLocalizeFunction,
  routingContext: IRoutingContext,
  parentUrlIds?: [IUrlId, ...IUrlId[]]
): IBreadcrumbOption[] => {
  if (!category) {
    return [];
  }
  parentUrlIds = parentUrlIds
    ? [...parentUrlIds, category.urlId]
    : [category.urlId];
  const breadcrumb = getBreadCrumbOption(
    category.name,
    parentUrlIds,
    strictLocalize,
    category.mostSpecificCategory.isProductFirstCategory ||
      category.isNewArrivalsSubCategory,
    routingContext
  );
  return recursivelyGetCategoryBreadcrumbs(
    category.subCategory,
    strictLocalize,
    routingContext,
    parentUrlIds
  ).concat(breadcrumb);
};
